import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

type RoleContextType = {
  role: string | null;
  loading: boolean;
};

const RoleContext = createContext<RoleContextType>({
  role: null,
  loading: true,
});

export const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadRole = async (userId: string | null) => {
    if (!userId) {
      setRole(null);
      setLoading(false);
      return;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error loading role:', error.message);
      setRole(null);
    } else {
      setRole(profile?.role ?? null);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Initial load
    supabase.auth.getUser().then(({ data }) => {
      loadRole(data.user?.id ?? null);
    });

    // Listen for auth changes
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        loadRole(session?.user?.id ?? null);
      }
    );

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  return (
    <RoleContext.Provider value={{ role, loading }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => useContext(RoleContext);
